import { useId, useMemo, useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { isThinSample, MIN_SAMPLE, pctText, type Change } from '@/lib/insights';
import { cn } from '@/lib/utils';

/**
 * Building blocks shared by the Insights tabs. Everything here renders from
 * theme tokens (no hard-coded colours), so it reads the same in dark and
 * light; charts are small inline SVGs for the same reason.
 */

// ── Definitions ───────────────────────────────────────────────────────────

/**
 * A metric's name with its definition: dotted underline, and the backend's
 * one-line definition in a tooltip on hover or keyboard focus (and to
 * screen readers through aria-describedby).
 */
export function MetricLabel({
  label,
  def,
  align = 'start',
  className,
}: {
  label: ReactNode;
  def?: string | null;
  /** Which edge the tooltip lines up with; use 'end' in right-aligned headers. */
  align?: 'start' | 'end';
  className?: string;
}): JSX.Element {
  const id = useId();
  if (!def) return <span className={className}>{label}</span>;
  return (
    <span className={cn('group/def relative inline-flex', className)}>
      <span
        tabIndex={0}
        aria-describedby={id}
        className="cursor-help rounded-sm underline decoration-muted-foreground/50 decoration-dotted underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {label}
      </span>
      <span
        id={id}
        role="tooltip"
        className={cn(
          'pointer-events-none absolute top-full z-30 mt-1.5 hidden w-64 max-w-[80vw] whitespace-normal rounded-lg border border-border bg-popover p-2.5 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground shadow-[var(--shadow-overlay)]',
          'group-focus-within/def:block group-hover/def:block',
          align === 'end' ? 'right-0' : 'left-0',
        )}
      >
        {def}
      </span>
    </span>
  );
}

// ── Values ────────────────────────────────────────────────────────────────

/** "—" for a number that can't be given, with the reason on hover and for screen readers. */
export function Unavailable({ reason }: { reason: string }): JSX.Element {
  return (
    <span className="cursor-help text-muted-foreground" title={reason}>
      —<span className="sr-only"> ({reason})</span>
    </span>
  );
}

/**
 * A rate, honest about its sample: null reads "—" with why, and fewer than
 * MIN_SAMPLE in the denominator reads greyed out ("too few to compare").
 */
export function RateValue({
  rate,
  n,
  of = 'replies',
  reason = 'Nothing to divide by in this period',
}: {
  rate: number | null | undefined;
  /** The denominator behind the rate. Omit to skip the sample check. */
  n?: number | null;
  /** What `n` counts, for the "only 4 replies" hint. */
  of?: string;
  /** Why the rate is missing when it's null. */
  reason?: string;
}): JSX.Element {
  if (rate == null) return <Unavailable reason={reason} />;
  if (n !== undefined && isThinSample(n)) {
    const hint = `Only ${n ?? 0} ${of}, fewer than ${MIN_SAMPLE}: too few to compare`;
    return (
      <span className="cursor-help font-mono text-muted-foreground/60" title={hint}>
        {pctText(rate)}
        <span className="sr-only"> ({hint})</span>
      </span>
    );
  }
  return <span className="font-mono">{pctText(rate)}</span>;
}

/** A count that opens the list behind it. Zero (or no link) is plain text. */
export function CountLink({
  value,
  to,
  title,
  className,
}: {
  value: number;
  to?: string | null;
  title?: string;
  className?: string;
}): JSX.Element {
  const text = value.toLocaleString();
  if (!to || value === 0) return <span className={cn('font-mono', className)}>{text}</span>;
  return (
    <Link
      to={to}
      title={title ?? 'Open these invites'}
      className={cn(
        'font-mono underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none',
        className,
      )}
    >
      {text}
    </Link>
  );
}

export function changeToneClass(tone: Change['tone']): string {
  switch (tone) {
    case 'good':
      return 'text-success-strong';
    case 'bad':
      return 'text-destructive-strong';
    default:
      return 'text-muted-foreground';
  }
}

export function ChangeText({ change, suffix }: { change: Change | null; suffix?: string }): JSX.Element | null {
  if (!change) return null;
  return (
    <span className={cn('font-mono text-[11px] tabular-nums', changeToneClass(change.tone))}>
      {change.text}
      {suffix ? <span className="font-sans text-muted-foreground"> {suffix}</span> : null}
    </span>
  );
}

// ── Tiles ─────────────────────────────────────────────────────────────────

export function KpiTile({
  icon: Icon,
  label,
  def,
  value,
  sub,
  change,
  to,
  emphasis,
}: {
  icon?: LucideIcon;
  label: string;
  def?: string | null;
  /** The figure; a node so "—" with a reason fits. */
  value: ReactNode;
  sub?: ReactNode;
  change?: Change | null;
  /** Makes the figure a link to the list behind it. */
  to?: string | null;
  emphasis?: boolean;
}): JSX.Element {
  return (
    <div className="panel min-w-0 rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-muted-foreground">
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-brand" strokeWidth={1.85} aria-hidden /> : null}
        <MetricLabel label={label} def={def} />
      </div>
      <div
        className={cn(
          'mt-2 truncate font-mono text-[1.5rem] font-medium leading-none tracking-[-0.04em] tabular-nums',
          emphasis && 'text-brand',
        )}
      >
        {to ? (
          <Link to={to} className="hover:underline focus-visible:underline focus-visible:outline-none">
            {value}
          </Link>
        ) : (
          value
        )}
      </div>
      <div className="mt-1.5 min-h-[1rem] text-xs text-muted-foreground">
        {change ? (
          <>
            <ChangeText change={change} /> vs the period before
          </>
        ) : null}
        {change && sub ? <br /> : null}
        {sub}
      </div>
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────

/**
 * A titled block. Titles are the question the block answers (or its
 * takeaway), never just the metric's name.
 */
export function Section({
  title,
  description,
  actions,
  children,
  id,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  id?: string;
}): JSX.Element {
  const autoId = useId();
  const headingId = id ?? autoId;
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 id={headingId} className="text-[0.9375rem] font-bold tracking-[-0.01em]">
            {title}
          </h2>
          {description ? <p className="mt-0.5 max-w-[75ch] text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A panel around a chart or a short list (never a data table). */
export function Panel({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={cn('panel rounded-xl p-4', className)}>{children}</div>;
}

export function Note({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'warning' }): JSX.Element {
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 text-xs',
        tone === 'warning' ? 'text-pending-strong' : 'text-muted-foreground',
      )}
    >
      {tone === 'warning' ? <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
      <span>{children}</span>
    </p>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" role="status">
      <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
      {label}
    </div>
  );
}

export function LoadError({ error, what }: { error: unknown; what: string }): JSX.Element {
  const message = error instanceof Error ? error.message : null;
  return (
    <div role="alert" className="panel rounded-xl p-5 text-sm">
      <p className="font-medium">Couldn’t load {what}.</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {message ? `${message}. ` : ''}Try again in a moment, or pick another period.
      </p>
    </div>
  );
}

/** A segmented control (mode toggles, group-by switches). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div role="group" aria-label={label} className="inline-flex items-stretch rounded-lg border border-input bg-card p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'inline-flex min-h-8 items-center rounded-md px-3 py-1 text-sm font-medium leading-tight transition-colors duration-[120ms] pointer-coarse:px-4',
            value === o.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Charts ────────────────────────────────────────────────────────────────

/**
 * Weekly bars for one series, scaled to its own max (small multiples share
 * the x axis, not the y). The latest week is solid; earlier weeks are a
 * tint, so "this week vs the run-up" reads at a glance.
 */
export function WeekBars({
  values,
  labels,
  format = (v) => v.toLocaleString(),
  name,
  height = 56,
}: {
  values: readonly number[];
  /** One label per value (week starts), for hover titles. */
  labels: readonly string[];
  format?: (v: number) => string;
  /** The series name, for the accessible label. */
  name: string;
  height?: number;
}): JSX.Element {
  const width = 240;
  const n = Math.max(values.length, 1);
  const slot = width / n;
  const barW = Math.max(2, slot * 0.68);
  const max = Math.max(...values, 0);
  const summary = values.map((v, i) => `${labels[i] ?? ''}: ${format(v)}`).join(', ');
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="block h-14 w-full"
      role="img"
      aria-label={`${name} per week. ${summary}`}
    >
      <line x1={0} x2={width} y1={height - 0.5} y2={height - 0.5} className="stroke-border" strokeWidth={1} />
      {values.map((v, i) => {
        const h = max > 0 ? Math.max(v > 0 ? 2 : 0, (v / max) * (height - 4)) : 0;
        const last = i === values.length - 1;
        return (
          <rect
            key={i}
            x={i * slot + (slot - barW) / 2}
            y={height - 1 - h}
            width={barW}
            height={h}
            rx={1.5}
            className={last ? 'fill-brand' : 'fill-brand/35'}
          >
            <title>{`${labels[i] ?? ''}: ${format(v)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/** Horizontal bars for a short ranked list (aging buckets, components). */
export function BarList({
  rows,
  ariaLabel,
}: {
  rows: readonly { key: string; label: ReactNode; value: number; display: ReactNode; sub?: ReactNode; tone?: 'brand' | 'warning' | 'muted' }[];
  ariaLabel: string;
}): JSX.Element {
  const max = Math.max(...rows.map((r) => r.value), 0);
  return (
    <ul aria-label={ariaLabel} className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">{r.label}</span>
            <span className="shrink-0 text-right font-mono text-xs tabular-nums">
              {r.display}
              {r.sub ? <span className="ml-1.5 font-sans text-muted-foreground">{r.sub}</span> : null}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full',
                r.tone === 'warning' ? 'bg-warning' : r.tone === 'muted' ? 'bg-muted-foreground/40' : 'bg-brand',
              )}
              style={{ width: `${max > 0 ? Math.max(r.value > 0 ? 1.5 : 0, (r.value / max) * 100) : 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Sorting ───────────────────────────────────────────────────────────────

export interface SortState<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

export function useSort<K extends string>(initial: SortState<K>): [SortState<K>, (key: K) => void] {
  const [sort, setSort] = useState<SortState<K>>(initial);
  const toggle = (key: K): void =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  return [sort, toggle];
}

/**
 * Sort rows by a value; nulls (no rate, no cost) always go last. For a rate
 * column, `sampleOf` gives the row's denominator: rates on a thin sample
 * sort after the comparable ones, so 1 of 2 never tops a leaderboard.
 */
export function useSorted<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K>,
  valueOf: (row: T, key: K) => number | string | null,
  sampleOf?: (row: T, key: K) => number | null | undefined,
): T[] {
  return useMemo(() => {
    const out = [...rows];
    const thin = (r: T): boolean => {
      const n = sampleOf?.(r, sort.key);
      return n !== undefined && isThinSample(n);
    };
    out.sort((a, b) => {
      const va = valueOf(a, sort.key);
      const vb = valueOf(b, sort.key);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const ta = thin(a);
      const tb = thin(b);
      if (ta !== tb) return ta ? 1 : -1;
      const cmp = typeof va === 'string' || typeof vb === 'string' ? String(va).localeCompare(String(vb)) : va - vb;
      return sort.dir === 'desc' ? -cmp : cmp;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
}

/** A sortable column header with an optional definition tooltip. */
export function SortHeader<K extends string>({
  label,
  k,
  sort,
  onSort,
  def,
  numeric = true,
}: {
  label: string;
  k: K;
  sort: SortState<K>;
  onSort: (k: K) => void;
  def?: string | null;
  numeric?: boolean;
}): JSX.Element {
  const active = sort.key === k;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined}
      className={cn('px-3 py-2.5 text-xs font-semibold text-muted-foreground/80', numeric && 'text-right')}
    >
      <span className={cn('inline-flex items-center gap-1', numeric && 'flex-row-reverse')}>
        <button
          type="button"
          onClick={() => onSort(k)}
          aria-label={`Sort by ${label}`}
          className={cn(
            'rounded px-0.5 font-mono text-[10px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            active ? 'text-foreground' : 'text-muted-foreground/50',
          )}
        >
          {active ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}
        </button>
        <MetricLabel label={label} def={def} align={numeric ? 'end' : 'start'} className={cn(active && 'text-foreground')} />
      </span>
    </th>
  );
}

/** A plain column header with an optional definition tooltip. */
export function Th({
  label,
  def,
  numeric = true,
}: {
  label: string;
  def?: string | null;
  numeric?: boolean;
}): JSX.Element {
  return (
    <th scope="col" className={cn('px-3 py-2.5 text-xs font-semibold text-muted-foreground/80', numeric && 'text-right')}>
      <MetricLabel label={label} def={def} align={numeric ? 'end' : 'start'} />
    </th>
  );
}
